import React, { ReactElement, useRef, useState } from 'react';
import { Box, ButtonBase, Popover, Typography } from '@mui/material';
import { Category } from 'enum/categories';
import { CategoryIcon } from 'components/ui/CategoryIcon';
import { COLORS, FONT } from './tokens';
import { MaterialSymbol } from './MaterialSymbol';

export interface CategoryDropdownOption {
  value: string;
  label: string;
}

export interface CategoryDropdownProps {
  value: string | null;
  options: CategoryDropdownOption[];
  onChange: (value: string | null) => void;

  allLabel?: string;
  width?: number;
}

export const CategoryDropdown = ({
  value,
  options,
  onChange,
  allLabel = 'All categories',
  width = 246,
}: CategoryDropdownProps): ReactElement => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const selectedLabel = options.find(o => o.value === value)?.label ?? allLabel;

  const handleSelect = (next: string | null): void => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen(true)}
        disableRipple
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          width: `${width}px`,
          maxWidth: '100%',
          height: '48px',
          pl: '14px',
          pr: '12px',
          gap: '8px',
          borderRadius: '8px',
          backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.filterBorder}`,
          flexShrink: 0,
          justifyContent: 'space-between',
          color: COLORS.navy,
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: '14px',
          transition: 'border-color 150ms ease',
          '&:hover': { borderColor: 'rgba(35,48,95,0.32)' },
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: '-2px',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: '1 0 0',
            minWidth: 0,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedLabel}
        </Box>
        <MaterialSymbol
          name="expand_more"
          size={18}
          color={COLORS.navy}
          sx={{
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Popover
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: '6px',
            borderRadius: '8px',
            border: '1px solid rgba(35,48,95,0.1)',
            boxShadow: '0px 10px 28px 0px rgba(13,18,38,0.17)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: '16px' }}>
          <Box
            role="listbox"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'flex-start',
              gap: '8px',
              width: '448px',
              maxWidth: '100%',
            }}
          >
            <CategoryGridItem
              label={allLabel}
              iconCategory="All"
              selected={value === null}
              onClick={() => handleSelect(null)}
            />
            {options.map(option => (
              <CategoryGridItem
                key={option.value}
                label={option.label}
                iconCategory={option.value}
                selected={option.value === value}
                onClick={() => handleSelect(option.value)}
              />
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const CategoryGridItem = ({
  label,
  iconCategory,
  selected,
  onClick,
}: {
  label: string;
  iconCategory: Category;
  selected: boolean;
  onClick: () => void;
}): ReactElement => (
  <ButtonBase
    onClick={onClick}
    disableRipple
    role="option"
    aria-selected={selected}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '220px',
      height: '48px',
      px: '10px',
      py: '8px',
      borderRadius: '8px',
      backgroundColor: selected ? 'rgba(35,48,95,0.08)' : 'transparent',
      transition: 'background-color 150ms ease',
      '&:hover': {
        backgroundColor: selected ? 'rgba(35,48,95,0.12)' : COLORS.hoverRow,
      },
      '&:focus-visible': {
        outline: `2px solid ${COLORS.navy}`,
        outlineOffset: '-2px',
      },
    }}
  >
    <Box sx={{ flexShrink: 0 }}>
      <CategoryIcon category={iconCategory} size={32} selected={selected} />
    </Box>
    <Typography
      sx={{
        flex: '1 0 0',
        minWidth: 0,
        textAlign: 'left',
        fontFamily: FONT,
        fontWeight: selected ? 600 : 500,
        fontSize: '14px',
        lineHeight: '20px',
        color: COLORS.navy,
      }}
    >
      {label}
    </Typography>
    {selected && <MaterialSymbol name="check" size={18} color={COLORS.navy} />}
  </ButtonBase>
);
